import { AHText, Box, ColorType } from "arkh";
import { useLazyLoadQuery, graphql } from "react-relay";

import { View } from "react-native";
import React, { useState, useDeferredValue, useMemo } from "react";

import { FormattedMessage } from "react-intl";

import { useWorkspaceId } from "../workspaceId";

import { WorkspaceDetailNavigationHeaderResponsive } from "../WorkspaceDetailNavigationHeader";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { TimeSeriesChartAndTable } from "./TimeSeriesChartAndTable";
import {
  AnalyticsTimeResolution,
  WorkspaceAnalyticsDetailQuery,
  WorkspaceAnalyticsDimensionType,
  WorkspaceAnalyticsMetric,
} from "./__generated__/WorkspaceAnalyticsDetailQuery.graphql";
import { WorkspaceAnalyticsStackScreenProps } from "./WorkspaceAnalyticsStack";
import { HeaderButton } from "@/src/components";
import { SegmentedControlNew } from "@/src/components/SegmentedControlNew/SegmentedControlNew";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useMetricToLabel } from "./metricToLabel";

function AnalyticsSubtitle() {
  return (
    <FormattedMessage
      description="Org Workspace Analytics description"
      defaultMessage="Upload files here to make them available to one, multiple, or all units within the current workspace."
    />
  );
}
export function WorkspaceAnalyticsDetailScreen(
  props: WorkspaceAnalyticsStackScreenProps<"WorkspaceAnalyticsDetail">
) {
  const wId = useWorkspaceId();

  return (
    <LazyQueryNetworkBoundrary>
      {(fk) => (
        <WorkspaceAnalyticsScreenComponent
          subTitle={<AnalyticsSubtitle />}
          workspaceId={wId}
          metric={props.route.params.metric}
          fetchKey={fk}
          onBack={() => {
            props.navigation.goBack();
          }}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function WorkspaceAnalyticsScreenComponent({
  fetchKey,
  workspaceId,
  metric,
  onBack,
}: {
  fetchKey: string;
  itemActionComponent?: React.ReactNode;
  subTitle?: React.ReactNode;
  workspaceId: string;
  color?: ColorType;
  metric: WorkspaceAnalyticsMetric;

  onBack: () => void;
}) {
  const [fk,] = useState(0);
  const intl_tokens = useIntlTokens();

  const [groupByDimension, setGroupByDimensio] =
    useState<WorkspaceAnalyticsDimensionType>("STORE");
  const [timeResolution, setTimeResolution] =
    useState<AnalyticsTimeResolution>("WEEK");
  const [chartType, setChartType] = useState<"lines" | "bars">("lines");

  const deferredTimeResolution = useDeferredValue(timeResolution);
  const deferredGroupByDimension = useDeferredValue(groupByDimension);
  const data = useLazyLoadQuery<WorkspaceAnalyticsDetailQuery>(
    graphql`
      query WorkspaceAnalyticsDetailQuery(
        $workspaceId: ID!
        $metric: WorkspaceAnalyticsMetric!
        $groupByDimension: WorkspaceAnalyticsDimensionType!
        $timeResolution: AnalyticsTimeResolution!
      ) {
        workspace(id: $workspaceId) @required(action: THROW) {
          analytics(metric: $metric) {
            valueUnit
            connection(
              groupByDimension: $groupByDimension
              timeResolution: $timeResolution
            ) {
              ...TimeSeriesChartAndTable_data
            }
          }
        }
      }
    `,
    {
      workspaceId,
      metric,
      groupByDimension: deferredGroupByDimension,
      timeResolution: deferredTimeResolution,
    },
    {
      fetchKey: fetchKey + fk,
      fetchPolicy: "store-and-network",
    }
  );

  const metricToLabel = useMetricToLabel();

  const [title, subTitle] = useMemo(
    () => metricToLabel(metric),
    [metric, metricToLabel]
  );
  if (!data.workspace.analytics) {
    return null;
  }
  return (
    <View style={{ flex: 1, width: "100%" }}>
      <WorkspaceDetailNavigationHeaderResponsive
        left={<HeaderButton type="WEB-BACK" onPress={onBack} />}
        right={null}
      />
      <Box
        style={{ alignItems: "center", flex: 1 }}
        spaceTop="large"
        spaceBottom="medium"
        spaceHorizontal="large"
      >
        <Box
          cornerRadius="large"
          style={{ overflow: "hidden", borderWidth: 1, flex: 1, width: "100%" }}
          borderColor="border-primary"
          color="surface"
        >
          <Box
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
            spaceHorizontal="large"
            spaceVertical="medium"
          >
            <Box>
              <AHText style={{ flex: 1 }} type="title-small">
                {title}
              </AHText>
              {subTitle ? (
                <AHText style={{ flex: 1 }} type="detail">
                  {subTitle}
                </AHText>
              ) : null}
            </Box>
            <Box style={{ flexDirection: "row" }} gap="medium">
              <SegmentedControlNew
                items={[
                  {
                    children: <AHText type="subtitle">Line</AHText>,
                    width: 40,
                    value: "lines" satisfies "lines" | "bars",
                  },
                  {
                    width: 40,
                    children: <AHText type="subtitle">Bar</AHText>,
                    value: "bars" satisfies "lines" | "bars",
                  },
                ]}
                selected={chartType}
                setSelected={(selected) => {
                  setChartType(selected);
                }}
              />
              <SegmentedControlNew
                items={[
                  {
                    children: <AHText type="subtitle">M</AHText>,
                    width: 40,
                    value: "MONTH" satisfies AnalyticsTimeResolution,
                  },
                  {
                    width: 40,
                    children: <AHText type="subtitle">W</AHText>,
                    value: "WEEK" satisfies AnalyticsTimeResolution,
                  },
                ]}
                selected={timeResolution}
                setSelected={(selected) => {
                  setTimeResolution(selected);
                }}
              />
              <SegmentedControlNew
                items={[
                  {
                    children: (
                      <AHText type="subtitle">{intl_tokens.Unit}</AHText>
                    ),
                    width: 100,
                    value: "STORE" satisfies WorkspaceAnalyticsDimensionType,
                  },
                  {
                    width: 100,
                    children: <AHText type="subtitle">Workspace</AHText>,
                    value:
                      "WORKSPACE_OWNER" satisfies WorkspaceAnalyticsDimensionType,
                  },
                ]}
                selected={groupByDimension}
                setSelected={(selected) => {
                  setGroupByDimensio(selected);
                }}
              />
            </Box>
          </Box>

          <TimeSeriesChartAndTable
            analytics={data.workspace.analytics.connection}
            type={chartType}
            unit={data.workspace.analytics.valueUnit}
            dimensionLabel={
              groupByDimension === "STORE" ? intl_tokens.Units : "Workspaces"
            }
          />
        </Box>
      </Box>
    </View>
  );
}
